/*
 * cobbler.fit — homepage HUD (snippets/cf-home-hud.liquid): the chapter chip,
 * the segmented progress line under the header, the "keep going" cue beside
 * the chip, and (Nov 2026) the story's beats and its Play button.
 *
 * Beats: the story's sections (the hero, the scan, the shoe) each say where
 * the scroll rests in them (root.__cfRests() → fractions of the section's
 * own scroll: a beat's words up and legible, its action at its peak). Let go
 * between two (wheel, trackpad, a swipe's momentum spent, the keys) and the
 * page glides on to the next one the way you were going — or back, if you
 * barely moved — at one speed everywhere, so every beat arrives the same way.
 * The cue shows after 3 s at a beat, or sooner when stuck between two.
 * Play steps through every beat on its own (a pause to read at each) and
 * ends on the two cards at the bottom; any touch, wheel or key stops it.
 *
 * Cheap on purpose: the page is measured on load / resize / when a section
 * changes height (ResizeObserver), never while scrolling. A scroll costs a
 * few comparisons against scrollY and, at most, a transform per segment.
 *
 * Waits for the homepage loader (it holds the page until the 3D is in), and
 * keeps quiet while the closet (on its shelf) shows its own first cue.
 */
(function (w, d) {
  'use strict';
  var hud = d.querySelector('[data-cf-hud]');
  if (!hud) return;
  var DEF = [];
  try { DEF = JSON.parse(hud.getAttribute('data-chapters') || '[]'); } catch (e) {}
  var bar = hud.querySelector('[data-hud-bar]'), chip = hud.querySelector('[data-hud-chip]');
  var numEl = hud.querySelector('[data-hud-num]'), nameEl = hud.querySelector('[data-hud-name]');
  var cueEl = hud.querySelector('.cf-hud-cue'), cueTs = [].slice.call(hud.querySelectorAll('[data-hud-cue-t]'));
  var touch = !!(w.matchMedia && w.matchMedia('(pointer: coarse)').matches);
  var still = !!(w.matchMedia && w.matchMedia('(prefers-reduced-motion: reduce)').matches);
  hud.classList.toggle('cf-hud--touch', touch);
  d.documentElement.classList.add('cf-hud-live');
  var closet = d.querySelector('[data-cf-closet]'), chooser = d.querySelector('[data-cf-choose]');
  var footer = d.querySelector('.shopify-section-group-footer-group, footer');
  var header = d.querySelector('.section-header');
  var IDLE_MS = 5000;   // the cue, away from the story's beats
  var IDLE_BEAT = 3000;  // …at a beat (its words up, its action at its peak)
  var IDLE_STUCK = 1500; // …between two beats, when the scroll didn't settle on one
  var playBtn = hud.querySelector('[data-hud-play]'), playT = hud.querySelector('[data-hud-play-t]');
  var cards = d.querySelector('.cf-cards');
  var html = d.documentElement;

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function pad(n) { return (n < 10 ? '0' : '') + n; }

  // ── measure ─────────────────────────────────────────────────────────────
  var CH = [], endY = Infinity, sig = '';
  function measure() {
    var sy = w.scrollY || 0, list = [];
    DEF.forEach(function (c) {
      var el = d.querySelector(c.sel);
      if (!el) return;
      var sec = (el.closest && el.closest('.shopify-section')) || el;
      var r = sec.getBoundingClientRect();
      if (!r.height) return;
      list.push({ name: c.name, intro: !!c.intro, top: r.top + sy, bottom: r.bottom + sy });
    });
    list.sort(function (a, b) { return a.top - b.top; });
    var out = [];
    list.forEach(function (c) {
      var p = out[out.length - 1];
      if (p && p.name === c.name) p.bottom = Math.max(p.bottom, c.bottom);
      else out.push(c);
    });
    // The intro (the path hero) has no number; the chapters count from 1 after it.
    var nth = 0;
    out.forEach(function (c) { c.num = c.intro ? '' : pad(++nth); });
    CH = out;
    endY = footer ? footer.getBoundingClientRect().top + sy : Infinity;
    // Rebuild the segments when the chapters change.
    var s = CH.map(function (c) { return c.name; }).join('|');
    if (s !== sig) {
      sig = s;
      bar.innerHTML = '';
      CH.forEach(function (c, i) {
        var b = d.createElement('button');
        b.type = 'button'; b.className = 'cf-hud-seg';
        b.setAttribute('aria-label', (c.num ? c.num + ' ' : '') + c.name);
        b.appendChild(d.createElement('i'));
        b.addEventListener('click', function () {
          var t = CH[i] ? CH[i].top : 0;
          w.scrollTo({ top: i === 0 ? 0 : t + 2, behavior: still ? 'auto' : 'smooth' });
        });
        bar.appendChild(b);
        c.fill = -1;
      });
    }
    var segs = bar.children;
    CH.forEach(function (c, i) {
      if (segs[i]) segs[i].style.flexGrow = Math.max(1, c.bottom - c.top).toFixed(0);
      c.fill = -1;
    });
    cur = -1;
    kick();
  }

  // ── each scroll ─────────────────────────────────────────────────────────
  var hudTop = -1, cur = -1, raf = 0, on = false, lastMove = Date.now(), idleT = 0, idle = false;
  // Ready once the page's loader (snippets/cf-home-loader) has let go.
  function ready() { return !d.querySelector('[data-cf-loader]:not(.is-done)'); }
  function update() {
    raf = 0;
    if (!CH.length) return;
    var sy = w.scrollY || 0, vh = w.innerHeight || 1, probe = sy + vh * 0.45;
    var i, idx = 0;
    for (i = 0; i < CH.length; i++) if (CH[i].top <= probe) idx = i;
    var segs = bar.children;
    for (i = 0; i < CH.length; i++) {
      var c = CH[i], span = c.bottom - c.top - vh, f;
      if (i < idx) f = 1;
      else if (i > idx) f = 0;
      else f = span > vh * 0.2 ? clamp((sy - c.top) / span, 0, 1) : clamp((probe - c.top) / (c.bottom - c.top), 0, 1);
      f = Math.round(f * 200) / 200;
      if (f !== c.fill && segs[i]) { c.fill = f; segs[i].firstChild.style.transform = 'scaleX(' + f + ')'; }
      if (segs[i]) segs[i].classList.toggle('is-cur', i === idx);
    }
    if (idx !== cur) {
      var first = cur < 0;
      cur = idx;
      numEl.textContent = CH[idx].num;
      nameEl.textContent = CH[idx].name;
      chip.classList.toggle('is-intro', !CH[idx].num);
      // The cue names what's next ("Next: The Fit"); plain "Scroll" at the last chapter.
      var nx = CH[idx + 1], tpl = cueEl && cueEl.getAttribute('data-hud-next-tpl');
      cueTs.forEach(function (el) { el.textContent = nx && tpl ? tpl.replace('{name}', nx.name) : el.getAttribute('data-dflt'); });
      if (!first && !still) { chip.classList.remove('is-roll'); void chip.offsetWidth; chip.classList.add('is-roll'); }
    }
    // Under the header's bottom edge, wherever that is (an announcement
    // bar above it scrolls away; the header sticks).
    var top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : -1;
    if (top >= 0 && top !== hudTop) { hudTop = top; hud.style.top = top + 'px'; }
    var want = ready() && (sy + vh * 0.55 < endY);
    if (want !== on) { on = want; hud.classList.toggle('is-on', on); }
  }
  function kick() { if (!raf) raf = requestAnimationFrame(update); }

  // ── the cue: after a pause, unless there's nothing more (or the closet is
  //    already saying it) ───────────────────────────────────────────────────
  function setIdle(v) { if (v !== idle) { idle = v; hud.classList.toggle('is-idle', v); } }
  function cueAllowed() {
    if (!on || !CH.length || playing || gliding) return false;
    var sy = w.scrollY || 0, vh = w.innerHeight || 1;
    if (sy + vh >= endY - 40) return false;                       // at the end
    if (chooser && sy < 10) return false;                            // the path hero's own hint is up
    if (closet && !closet.hasAttribute('data-solo')) {
      var c = closet.classList;
      if (!c.contains('cf-closet--scrolled')) return false;      // its own first cue is up
      if (touch && c.contains('cf-closet--front')) return false; // the pair's card sheet is up
    }
    var a = d.activeElement;
    if (a && /INPUT|TEXTAREA|SELECT/.test(a.tagName)) return false;
    return true;
  }
  // How long to wait before the cue, from where the scroll is now.
  function idleFor() {
    var at = beatAt(w.scrollY || 0);
    return at === 'rest' ? IDLE_BEAT : at === 'between' ? IDLE_STUCK : IDLE_MS;
  }
  function moved() {
    lastMove = Date.now();
    setIdle(false);
    clearTimeout(idleT);
    idleT = setTimeout(function check() {
      if (gliding || playing) { idleT = setTimeout(check, 400); return; }
      setIdle(cueAllowed());
    }, idleFor());
  }

  // ── the beats ───────────────────────────────────────────────────────────
  // Measured fresh each time they're needed (a few rects): the path hero
  // reorders the sections and they change height (the scan's intro, the
  // shoe's last step) as the path is set.
  var STORY = '[data-cf-choose], [data-cf-paper], [data-cf-closet]';
  function rests() {
    var sy = w.scrollY || 0, vh = w.innerHeight || 1, out = [];
    [].forEach.call(d.querySelectorAll(STORY), function (el) {
      if (typeof el.__cfRests !== 'function') return;
      var r = el.getBoundingClientRect(), span = r.height - vh;
      if (!r.height || span <= 0) return;
      var fr = [];
      try { fr = el.__cfRests() || []; } catch (e) {}
      fr.forEach(function (f) { out.push(Math.round(r.top + sy + f * span)); });
    });
    out.sort(function (a, b) { return a - b; });
    // (Two sections meeting — the end of one is the start of the next.)
    return out.filter(function (y, i) { return !i || y - out[i - 1] > 24; });
  }
  // 'rest' (on a beat), 'between' (inside the story, off a beat) or 'off'.
  function beatAt(y) {
    var R = rests(), vh = w.innerHeight || 1;
    if (!R.length || y < R[0] - 4 || y > R[R.length - 1] + 4) return 'off';
    for (var i = 0; i < R.length; i++) if (Math.abs(R[i] - y) <= Math.max(6, vh * 0.03)) return 'rest';
    return 'between';
  }
  // The two cards at the bottom: where Play ends.
  function cardsY() {
    if (!cards) return null;
    var sy = w.scrollY || 0, vh = w.innerHeight || 1, r = cards.getBoundingClientRect();
    var top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0, room = vh - top;
    var y = r.height < room ? r.top + sy - top - (room - r.height) / 2 : r.top + sy - top - 8;
    return Math.max(0, Math.round(y));
  }

  // One glide, ours (eased in and out); any input takes over at once.
  var gliding = false, glideId = 0;
  function easeIO(k) { return k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2; }
  // One speed for every glide: a beat of the story (BEAT svh of scroll) in BEAT_MS.
  var BEAT_MS = 1700;
  function msFor(dy, rate) {
    var vh = w.innerHeight || 1, beat = 1.3 * vh;
    return still ? 0 : Math.round(clamp(Math.abs(dy) / beat * BEAT_MS * (rate || 1), 420, 3200));
  }
  function glideTo(to, ms, done) {
    var id = ++glideId, from = w.scrollY || 0, dist = to - from, t0 = 0;
    if (Math.abs(dist) < 1 || !ms) { if (Math.abs(dist) >= 1) w.scrollTo(0, to); gliding = false; if (done) done(true); return; }
    gliding = true;
    html.classList.add('cf-beat-gliding');
    requestAnimationFrame(function step(now) {
      if (id !== glideId) return;
      if (!t0) t0 = now;
      var k = Math.min(1, (now - t0) / ms);
      w.scrollTo(0, from + dist * easeIO(k));
      if (k < 1) { requestAnimationFrame(step); return; }
      gliding = false;
      html.classList.remove('cf-beat-gliding');
      if (done) done(true);
    });
  }
  function stopGlide() {
    if (!gliding) return;
    glideId++; gliding = false;
    html.classList.remove('cf-beat-gliding');
  }

  // Letting go between beats: on to the next one the way you were going.
  var touching = false, dir = 0, lastY = w.scrollY || 0, snapT = 0;
  function snapOK() {
    if (still || playing || gliding || !on) return false;
    if (html.classList.contains('cf-gliding')) return false;           // the hero's own glide
    var a = d.activeElement;
    if (a && /INPUT|TEXTAREA|SELECT/.test(a.tagName)) return false;
    return true;
  }
  function snap() {
    if (touching || !snapOK()) return;
    var y = w.scrollY || 0, R = rests();
    if (R.length < 2 || y <= R[0] + 2 || y >= R[R.length - 1] - 2) return;
    var i = 0;
    while (i < R.length - 1 && R[i + 1] <= y) i++;
    var a = R[i], b = R[i + 1];
    if (y - a < 3 || b - y < 3) return;
    var fr = (y - a) / (b - a);
    // A nudge carries on (8% of the way is enough); a fling that ran out
    // most of the way, too; barely moved back, it goes back.
    var to = dir > 0 ? (fr > 0.08 ? b : a) : dir < 0 ? (fr < 0.92 ? a : b) : (fr < 0.5 ? a : b);
    if (pickFirst(to, snap)) return;
    glideTo(to, msFor(to - y, 0.6));
  }
  // Heading past the choice with nothing picked: pick first (as the hero
  // does when the scroll crosses it) — that reorders the path's sections,
  // so where the next beat is is only known after.
  function pickFirst(to, then) {
    var ch = chooser && chooser.__cfChoose, R = rests();
    if (!ch || ch.picked() || R.length < 2 || to <= R[1] + 6) return false;
    ch.pick();
    setTimeout(then, 80);
    return true;
  }
  function scheduleSnap() {
    clearTimeout(snapT);
    if (touching) return;
    snapT = setTimeout(snap, touch ? 220 : 160);
  }
  w.addEventListener('scroll', function () {
    var y = w.scrollY || 0;
    if (!gliding && y !== lastY) dir = y > lastY ? 1 : -1;
    lastY = y;
    kick();
    if (!gliding) { moved(); scheduleSnap(); }
  }, { passive: true });
  // Input of their own: a glide (or Play) lets go at once.
  function input(e) {
    if (e && e.type === 'keydown' && e.target === playBtn) return;
    if (playing) stopPlay();
    stopGlide();
    moved();
  }
  ['wheel', 'keydown'].forEach(function (t) { w.addEventListener(t, input, { passive: true }); });
  w.addEventListener('pointerdown', function (e) { if (!playBtn || !playBtn.contains(e.target)) input(e); else moved(); }, { passive: true });
  w.addEventListener('touchstart', function (e) { touching = true; clearTimeout(snapT); if (!playBtn || !playBtn.contains(e.target)) input(e); }, { passive: true });
  w.addEventListener('touchend', function () { touching = false; scheduleSnap(); }, { passive: true });
  w.addEventListener('touchcancel', function () { touching = false; scheduleSnap(); }, { passive: true });

  // ── Play: through every beat, ending on the two cards ──────────────────
  var playing = false, playTimer = 0, DWELL = 2600;
  function setPlayUi() {
    if (!playBtn) return;
    playBtn.classList.toggle('is-playing', playing);
    playBtn.setAttribute('aria-pressed', playing ? 'true' : 'false');
    var t = playBtn.getAttribute(playing ? 'data-t-pause' : 'data-t-play') || '';
    if (playT) playT.textContent = t;
    playBtn.setAttribute('aria-label', t);
  }
  function stopPlay() {
    playing = false;
    clearTimeout(playTimer);
    stopGlide();
    setPlayUi();
  }
  function playStep() {
    if (!playing) return;
    var y = w.scrollY || 0, R = rests(), ch = chooser && chooser.__cfChoose;
    // At the choice and nothing picked yet: pick, as scrolling on would.
    if (ch && !ch.picked() && R.length > 1 && y >= R[1] - 6) {
      ch.pick();
      playTimer = setTimeout(playStep, 450);   // (the path's sections are reordered under it)
      return;
    }
    var next = null;
    for (var i = 0; i < R.length; i++) if (R[i] > y + 6) { next = R[i]; break; }
    if (next == null) {
      var cy = cardsY();
      if (cy != null && cy > y + 6) glideTo(cy, msFor(cy - y, 0.35), function () { stopPlay(); });
      else stopPlay();
      return;
    }
    glideTo(next, msFor(next - y), function () {
      if (!playing) return;
      playTimer = setTimeout(playStep, DWELL);
    });
  }
  function startPlay() {
    if (!ready()) return;
    playing = true;
    setIdle(false);
    setPlayUi();
    var y = w.scrollY || 0, cy = cardsY();
    // From the end: back to the top first.
    if (cy != null && y >= cy - 6) { glideTo(0, msFor(y, 0.3), function () { if (playing) playTimer = setTimeout(playStep, 900); }); return; }
    var at = beatAt(y);
    playTimer = setTimeout(playStep, at === 'rest' ? 600 : 0);
  }
  if (playBtn) {
    playBtn.addEventListener('click', function (e) { e.preventDefault(); if (playing) stopPlay(); else startPlay(); });
    setPlayUi();
  }
  w.addEventListener('resize', measure);
  w.addEventListener('load', measure);
  w.addEventListener('cf:header', kick);
  if (typeof ResizeObserver !== 'undefined') {
    var mc = d.getElementById('MainContent');
    var rt = 0;
    if (mc) new ResizeObserver(function () { clearTimeout(rt); rt = setTimeout(measure, 120); }).observe(mc);
  }
  // Until the closet's loader lets go, look again now and then.
  if (closet && !ready()) {
    var poll = setInterval(function () { if (ready()) { clearInterval(poll); measure(); moved(); } }, 400);
  }
  measure();
  moved();
})(window, document);
